import { buffer as turfBuffer, featureCollection, union, Feature, geometry, Geometry } from "@turf/turf";
import {
    Polygon,
    MultiPolygon,
    Properties,
    Position,
    polygon,
    multiPolygon
  } from "@turf/helpers";
import rewind from "@turf/rewind";
import bbox from "@turf/bbox";
import buffer from "@turf/buffer";
import simplify from "@turf/simplify";
import cleanCoords from "@turf/clean-coords";
import unkinkPolygon from "@turf/unkink-polygon";
import * as turf from '@turf/turf';
import { WebMercatorViewport } from "react-map-gl";
import { Field } from "../types/types";
import { defaultViewport } from '../constants';
export const INTERNAL_FIELD_ID = '__id';

export const METERS_TO_ACRES = 0.00024711;

// Structured report for geometry repair failures
export interface RepairIssue {
    id?: any;
    reason: string;
    errorMessage?: string;
    geometryType?: string;
    geometry?: any;
    properties?: any;
    coordSummary?: any;
    bbox?: number[];
    polygonIndex?: number; // for MultiPolygon pieces
}

const __repairIssues: RepairIssue[] = [];
export function resetRepairIssues() {
    __repairIssues.length = 0;
}
export function getRepairIssues(): RepairIssue[] {
    // return a shallow copy to avoid external mutation
    return [...__repairIssues];
}

function preprocessPolygon(
  raw: Feature<Polygon, any>
): Feature<Polygon, any> {
  // 1. fix ring orientation (optional but often helps)
  const wound = rewind(raw, { reverse: true });

  // 2. zero-distance buffer to force a valid geometry
  const buffed = buffer(wound, 0, { units: "meters" }) as Feature<
    Polygon,
    any
  >;

  return buffed;
}
/**
* Runs “snap / clean / simplify / unkink” sequence
* to clean-up vertices that are too close (likely from 
* parametric arcs), also handles donuts (inner rings) 
* that intersect outer ring. 
*/
function repairOuterRing(ringFeat: Feature<Polygon, Properties>): Feature<Polygon, Properties> | null {
    //return ringFeat;
    // 0) strip exact dupes
    console.log('repairOuterRing ringFeat', ringFeat);
    const _ringFeat = ringFeat; //preprocessPolygon(ringFeat);
    //if (!_ringFeat || !_ringFeat.geometry || !_ringFeat.geometry.coordinates) {
     //   console.warn('repairOuterRing: Invalid ring feature', _ringFeat, ringFeat);
     //   return null;
    //}
    console.log('repairOuterRing _ringFeat', _ringFeat);
    const initClean = cleanCoords(_ringFeat);
  
    // 1) zero‐distance buffer
    const buffered = buffer(initClean, 0, { units: "meters" }) as Feature<Polygon, Properties>;
  
    // 2) clean again
    const cleaned = cleanCoords(buffered);
  
    // 3) simplify tiny wiggles
    const simplified = simplify(cleaned, {
      tolerance: 1e-6,
      highQuality: true,
      mutate: false
    }) as Feature<Polygon, Properties>;
  
    // 4) attempt unkink
    let parts;
    try {
      parts = unkinkPolygon(simplified);
    } catch {
      return buffered;
    }
  
    // if no self-intersections > keep the simplified ring
    if (parts.features.length === 0) {
      return simplified;
    }
  
    // otherwise union the split pieces back
    let merged = parts.features[0] as Feature<Polygon, Properties>;
    for (let i = 1; i < parts.features.length; i++) {
      merged = union(merged, parts.features[i]) as Feature<Polygon, Properties>;
    }
    return merged;
}
  
/**
* Repairs a Polygon or MultiPolygon but preserves donut holes.
*/
export function repairGeom(
    feat: Feature<Polygon|MultiPolygon, Properties>
    ): Feature<Polygon|MultiPolygon, Properties> | null {
    // First, attempt to remove any consecutive duplicate vertices
    // Guard in case upstream geometry is too degenerate for cleanCoords
    let cleanedFeat: Feature<Polygon|MultiPolygon, Properties>;
    try {
        cleanedFeat = cleanCoords(feat as any) as Feature<Polygon|MultiPolygon, Properties>;
    } catch (e: any) {
        // Record the error and bail out
        let bboxArr: number[] | undefined = undefined;
        try { bboxArr = bbox(feat as any) as any; } catch {}
        const coordSummary = (() => {
            const g: any = (feat as any).geometry;
            if (!g) return undefined;
            if (g.type === 'Polygon') return { rings: g.coordinates.length, outerLen: g.coordinates[0]?.length };
            if (g.type === 'MultiPolygon') return { polys: g.coordinates.length, outerLen0: g.coordinates[0]?.[0]?.length };
            return undefined;
        })();
        __repairIssues.push({
            id: (feat as any).id ?? (feat as any).properties?.id,
            reason: 'cleanCoords failed',
            errorMessage: e?.message || String(e),
            geometryType: (feat as any).geometry?.type,
            geometry: (feat as any).geometry,
            properties: (feat as any).properties,
            coordSummary,
            bbox: bboxArr,
        });
        return null;
    }
    const geom = cleanedFeat.geometry;

    // Helper: dedupe consecutive positions in a linear ring, keep closure
    const positionsEqual = (a: Position, b: Position, eps = 0) => {
        const ax = a[0], ay = a[1];
        const bx = b[0], by = b[1];
        return Math.abs(ax - bx) <= eps && Math.abs(ay - by) <= eps;
    };
    const dedupeRing = (ring: Position[], eps = 0): Position[] => {
        if (!ring || ring.length === 0) return ring;
        const out: Position[] = [];
        // iterate through all points
        for (let i = 0; i < ring.length; i++) {
            const pt = ring[i];
            const prev = out[out.length - 1];
            if (!prev || !positionsEqual(prev, pt, eps)) {
                out.push(pt);
            }
        }
        // ensure closure (first == last)
        if (out.length > 0 && !positionsEqual(out[0], out[out.length - 1], eps)) {
            out.push(out[0]);
        }
        return out;
    };

    if (geom.type === "Polygon") {
        // split into outer + holes
        const [outerRing, ...holes] = geom.coordinates;
        const outerD = dedupeRing(outerRing);
        const holesD = holes.map(h => dedupeRing(h)).filter(r => r && r.length >= 4);
        if (!outerD || outerD.length < 4) {
            let bboxArr: number[] | undefined = undefined;
            try { bboxArr = bbox(cleanedFeat as any) as any; } catch {}
            __repairIssues.push({
                id: (cleanedFeat as any).id ?? (cleanedFeat as any).properties?.id,
                reason: 'invalid outer ring after dedupe',
                geometryType: 'Polygon',
                geometry: (cleanedFeat as any).geometry,
                properties: (cleanedFeat as any).properties,
                coordSummary: { outerLen: outerD?.length, holes: holesD.length },
                bbox: bboxArr,
            });
            return null;
        }
        // wrap it as its own Poly-feature
        const outerFeat = polygon([outerD], cleanedFeat.properties);
        // run your ring-repair
        const cleanedOuterRepair = repairOuterRing(outerFeat);
        const cleanedOuter =  cleanedOuterRepair ? cleanedOuterRepair.geometry.coordinates[0] : null;
        // rebuild a new Polygon with that cleaned outer ring + original holes
        if (cleanedOuter) {
            return polygon([cleanedOuter, ...holesD], cleanedFeat.properties);
        } else {
            let bboxArr: number[] | undefined = undefined;
            try { bboxArr = bbox(cleanedFeat as any) as any; } catch {}
            __repairIssues.push({
                id: (cleanedFeat as any).id ?? (cleanedFeat as any).properties?.id,
                reason: 'outer ring repair failed',
                geometryType: 'Polygon',
                geometry: (cleanedFeat as any).geometry,
                properties: (cleanedFeat as any).properties,
                coordSummary: { outerLen: outerD.length, holes: holesD.length },
                bbox: bboxArr,
            });
            return null;
        }
    }

    // MultiPolygon: do the same for each part
    if (geom.type === "MultiPolygon") {
        const newCoords = geom.coordinates.map((polyCoords, idx) => {
            const [outerRing, ...holes] = polyCoords;
            const outerD = dedupeRing(outerRing);
            const holesD = holes.map(h => dedupeRing(h)).filter(r => r && r.length >= 4);
            if (!outerD || outerD.length < 4) {
                let bboxArr: number[] | undefined = undefined;
                try { bboxArr = bbox(cleanedFeat as any) as any; } catch {}
                __repairIssues.push({
                    id: (cleanedFeat as any).id ?? (cleanedFeat as any).properties?.id,
                    reason: 'invalid outer ring after dedupe',
                    geometryType: 'MultiPolygon',
                    geometry: (cleanedFeat as any).geometry,
                    properties: (cleanedFeat as any).properties,
                    coordSummary: { polygonIndex: idx, outerLen: outerD?.length, holes: holesD.length },
                    bbox: bboxArr,
                    polygonIndex: idx,
                });
                return null;
            }
            const outerFeat = polygon([outerD], cleanedFeat.properties);
            const cleanedOuterRepair = repairOuterRing(outerFeat);
            const cleanedOuter = cleanedOuterRepair ? cleanedOuterRepair.geometry.coordinates[0] : null;
            if (!cleanedOuter) {
                // If cleanedOuter is null, skip this polygon part
                let bboxArr: number[] | undefined = undefined;
                try { bboxArr = bbox(cleanedFeat as any) as any; } catch {}
                __repairIssues.push({
                    id: (cleanedFeat as any).id ?? (cleanedFeat as any).properties?.id,
                    reason: 'outer ring repair failed',
                    geometryType: 'MultiPolygon',
                    geometry: (cleanedFeat as any).geometry,
                    properties: (cleanedFeat as any).properties,
                    coordSummary: { polygonIndex: idx, outerLen: outerD.length, holes: holesD.length },
                    bbox: bboxArr,
                    polygonIndex: idx,
                });
                return null;
            } else {
                return [cleanedOuter, ...holesD];
            }
        }).filter(coords => coords !== null);
        const filteredCoords = newCoords.filter((coords): coords is Position[][] => coords !== null);
        if (filteredCoords.length > 0) {
            return multiPolygon(filteredCoords, cleanedFeat.properties);
        } else {
            let bboxArr: number[] | undefined = undefined;
            try { bboxArr = bbox(cleanedFeat as any) as any; } catch {}
            __repairIssues.push({
                id: (cleanedFeat as any).id ?? (cleanedFeat as any).properties?.id,
                reason: 'all multipolygon parts invalid after repair',
                geometryType: 'MultiPolygon',
                geometry: (cleanedFeat as any).geometry,
                properties: (cleanedFeat as any).properties,
                coordSummary: { polys: (cleanedFeat as any).geometry?.coordinates?.length },
                bbox: bboxArr,
            });
            return null;
        }
    }

    // fallback (shouldn't happen once typed correctly)
    return feat as any;
}

  

export const zoomToFeatures = (geojson: any, prevViewport:any, width: number, height: number, padding: number) => {
    let nextViewport = new WebMercatorViewport({
        ...prevViewport,
        width,
        height
    });

    if(geojson.features.length) {
        const [minLng, minLat, maxLng, maxLat] = bbox(geojson);
        console.log('zoomToFeatures', minLng, minLat, maxLng, maxLat);
        nextViewport = nextViewport.fitBounds([
            [minLng, minLat],
            [maxLng, maxLat]
        ], {
            padding
        });
    }

    return nextViewport;
}

export const zoomToFeature = (feature: any, viewport: any, width: number, height: number, padding: number) => {
    const [minLng, minLat, maxLng, maxLat] = bbox(feature.geometry);

    // help control the web mercator exception based on small small width
    const newPadding = width < 600 ? 85 : padding;

    const nextViewport = new WebMercatorViewport({
        ...viewport,
        width,
        height,
        pitch: 0,
        bearing: 0
    }).fitBounds([
        [minLng, minLat],
        [maxLng, maxLat]
    ], {
        padding: newPadding
    });

    return nextViewport;
}

export const createDataSource = (features: any[]): any => {
    let geojson: any = {
        type: "FeatureCollection",
        crs: { "type": "name", "properties": { "name": "urn:ogc:def:crs:OGC:1.3:CRS84" } },
        features: features ? features.map((f: any) => {
            return f.feature;
        }) : []
    };

    return geojson;
}

export const createFeatureCollection = (features: any[]) => {
    
    let geojson: any = {
        type: "FeatureCollection",
        crs: { "type": "name", "properties": { "name": "urn:ogc:def:crs:OGC:1.3:CRS84" } },
        features: features ? features.map((f: any) => {
            return f;
        }) : []
    };

    return geojson;
}

export const createFeatureCollectionFromFieldsGeometry = (features: any[]) => {
    console.log("Y050724 features", features);
    let geojson: any = {
        type: "FeatureCollection",
        crs: { "type": "name", "properties": { "name": "urn:ogc:def:crs:OGC:1.3:CRS84" } },
        features: features ? features.map((f: any) => {
            f["geometry"] = f.geometry;
            return f;
        }) : []
    };

    return geojson;
}
export const createFeatureCollectionFromFields = (features: any[]) => {
    console.log("Y050724 features", features);
    let geojson: any = {
        type: "FeatureCollection",
        crs: { "type": "name", "properties": { "name": "urn:ogc:def:crs:OGC:1.3:CRS84" } },
        features: features ? features.map((f: any) => {
            f["geometry"] = f.geom;
            return f;
        }) : []
    };

    return geojson;
}

export const createFeatureFromField = (field: Field): any => {
    const {geom, ...properties} = field;
    return {
        type: "Feature",
        "properties": {...properties},
        "geometry": geom
    };
}

export const createCentroidFeatureFromField = (field: Field, dateString:string | null): any => {
    const {/*centroid,*/ ...properties} = field; // some centroid features are offcentered
    const {name, id} = properties;
    //let centroidTurf = turf.centroid(field.geom); // some features are offcentered
    const centerOfMass = turf.centerOfMass(field.geom);
    //const pointOnFeature = turf.pointOnFeature(field.geom);
    //console.log("B071925 centroid", centroid);
    //console.log("B072025 properties", properties);
    //console.log("C072225 dateString", dateString);
    const pct_cloud_time = dateString; //properties.cloud_cover ? properties.cloud_cover["Percent Cloud_time"] : null;
    const pct_cloud_value = (properties.cloud_cover && dateString) 
        ? properties.cloud_cover[dateString] 
            ? properties.cloud_cover[dateString]
            : 0
        : 0;
    //const cloud_cover = properties.cloud_cover ? properties.cloud_cover : null;

    //console.log("BB071925 F");
    //console.log("BB071925 pct_cloud_time", pct_cloud_time);
    //console.log("BB072025 pct_cloud_value", pct_cloud_value);
    //console.log("BB071925 cloud_cover", cloud_cover);
    return {
        type: "Feature",
        "properties": {name, id, pct_cloud_time, pct_cloud_value/*, cloud_cover*/},
        "geometry": centerOfMass.geometry
    };
}

export const __createCentroidFeatureFromGeom = (geom: any, props:any): any => {
    let centroid = turf.centroid(geom);
    const centerOfMass = turf.centerOfMass(geom);
    const pointOnFeature = turf.pointOnFeature(geom);

    console.log('A051724 props.label', props.label);
    //console.log('A051724 centroid pip', centroid);
    console.log('A051724 centerMass PIP', turf.booleanPointInPolygon(centerOfMass, geom));
    console.log('A051724 centroid PIP', turf.booleanPointInPolygon(centroid, geom));
    console.log('A051724 pointOnFeature PIP', turf.booleanPointInPolygon(pointOnFeature, geom));
    console.log('A051724 pointOnFeature WITHIN', turf.booleanWithin(pointOnFeature, geom));
    let __centroid = pointOnFeature;
    if (turf.booleanWithin(pointOnFeature, geom) === false && turf.booleanPointInPolygon(pointOnFeature, geom) === true){
        /*const directions = [0, 45, 90, 135, 180, 225, 270, 315, 360];
        //const directions = ['n', 's', 'e', 'w', 'ne', 'se', 'sw', 'nw'];
        for (const bearing of directions) {
            //const bearing = turf.bearingToAzimuth(direction);
            const movedPoint = turf.destination(pointOnFeature, (50 / 1000), bearing);
            if (turf.booleanWithin(movedPoint, geom)) {
                console.log(`AA051724 Point is within the geometry in the ${bearing} direction.`);
                centroid = movedPoint;
            } else {
                console.log(`Point is outside the geometry in the ${bearing} direction.`);
            }
        }*/
        createCentroidFeatureFromGeom(geom, props);
    }
    return {
        type: "Feature",
        "properties": geom.properties,// {name, id},
        "geometry": centroid.geometry
    };
}

export const createCentroidFeatureFromGeom = (_geom: any, props:any): any => {
    let furthestPoint:any = null;
    let geom:any = _geom;
    try {
        const line = turf.polygonToLine(_geom);
        const lineChunk = turf.lineChunk(line, 0.025, {units: 'kilometers'});
        console.log("A051825 lineChunk" , lineChunk)
        let coords = turf.coordAll(lineChunk);
        console.log("A051825 coords" , coords)
        let _coords = coords.map((c:any) => {
            return [c[0], c[1]];
        })
        console.log("A051825 _coords", _coords);
        const _line = turf.lineString(_coords);
        const __geom = turf.lineToPolygon(_line);
        console.log("A051825 line", line);
        console.log("A051825 __geom", __geom);
        geom = __geom; //turf.lineToPolygon(lineChunk);
        const vertices = turf.coordAll(geom);
        const bbox = turf.bbox(geom);
        const grid = turf.pointGrid(bbox, 0.025, { units: 'kilometers' }); // Adjust the grid size for accuracy and performance
        const pointsInside = turf.pointsWithinPolygon(grid, geom);

        let maxDistance = -Infinity;

        turf.featureEach(pointsInside, (point: any) => {
            const nearestVertexDistance = vertices.reduce((minDist: number, vertex: any) => {
                const dist = turf.distance(point, turf.point(vertex), { units: 'kilometers' });
                return Math.min(minDist, dist);
            }, Infinity);

            if (nearestVertexDistance > maxDistance) {
                maxDistance = nearestVertexDistance;
                furthestPoint = point;
            }
        });
    } catch (e) {
        console.log("A051825 e", e);
        furthestPoint = turf.pointOnFeature(_geom);
    }

    return {
        type: "Feature",
        properties: geom.properties,
        geometry: furthestPoint ? furthestPoint.geometry : null,
    };
};


export const createPolygonFeatureFromField = (field: Field): any => {
    const {geom, ...properties} = field;

    let geometry = {...field.geom};

    if(geometry.type === 'MultiPolygon') {
        geometry.type = 'Polygon';
        geometry.coordinates = geometry.coordinates[0]
    }
    console.log('Z050624 geometry', geometry);
    return {
        type: "Feature",
        "properties": {...properties},
        "geometry": geometry
    };
}

export const createPolygonFeatureFromFieldMulti = (field: Field): any => {
    const {geom, ...properties} = field;

    let geometry = {...field.geom};
    console.log("Z050724 field", field);
    let features = geometry.coordinates.map((coords: any, i:number) => {
        geometry.type = 'Polygon';
        geometry.coordinates = coords;
        console.log("Z050724 coords", coords);
        console.log("Y050724 geometry", geometry);
        console.log("Y050724 {...geometry}", {...geometry});
        return {
            type: "Feature",
            "properties": {
                ...properties,
                fragmentIndex: i,
            },
            "geometry": {...geometry},
        };
    });
    console.log("Y050724 features", features);
    return createFeatureCollectionFromFieldsGeometry([...features]);
}

export const createPolygonFeatureFromZones = (zones: any): any => {
    console.log('041924 zones', zones);
    const zoneFeatures = zones.map((zone: any) => {
        return {
            type: "Feature",
            "properties": {...zone},
            "geometry": zone.geom
        };
    })
    console.log('041924 zoneFeatures', zoneFeatures);
    return {"features":zoneFeatures};
}

export const coordsFromViewportParam = (param: string) => {
    return param.replace('@', '').split(',');
}

export const getViewport = (viewport: string, fields: any = null, width: any = null, height: any = null, caller:string = "") => {
    //console.log('013024 getViewport', viewport, fields, width, height);
    //console.log('013024 viewport', viewport, caller);
    let v = {...defaultViewport};

    if (fields?.length > 0){
        let _fields = createFeatureCollectionFromFields(fields);
        console.log('013024 _fields', _fields);
        const [minLng, minLat, maxLng, maxLat] = bbox(_fields);
        console.log('013024 zoomToFeatures', minLng, minLat, maxLng, maxLat);
        let viewportObj = zoomToFeatures(_fields, viewport, width, height, 100)
        viewport = `@${viewportObj.latitude},${viewportObj.longitude},${viewportObj.zoom}`;
    }

    if(viewport) {
        const coords = coordsFromViewportParam(viewport);

        if(coords.length === 3) {
           const [lat, lon, zoom] = coords;

            v.latitude =  parseFloat(lat);
            v.longitude = parseFloat(lon);
            v.zoom = parseFloat(zoom);
        }
    }
    //console.log('013024 v', v, caller);
    return v;
}

export const getFeaturesByType = (type: 'Polygon' | 'MultiPolygon', collection: any) => {
    console.log(type);
    if(collection.type === "FeatureCollection") {
        return collection.features.filter((f:any) => f.geometry.type === type);
    }
}

export const calculateAcres = (feature: Feature): number => {
    return turf.area(feature) * METERS_TO_ACRES
}

export const toMultiPolygonGeom = (feature: Feature) => {
    let geometry = {...feature.geometry} as any;

    if(feature.geometry.type !== 'MultiPolygon') {
        geometry.coordinates = [geometry.coordinates];      
        geometry.type = "MultiPolygon";
    }

    geometry.crs = {
        "type": "name",
        "properties": {
          "name": "urn:ogc:def:crs:EPSG::4326"
        }
    };

    return geometry;
}